// Dense, virtualized, keyboard-driven table over local rows. Sorting and filtering are in-memory,
// so they're instant at any size we'll have. ↑/↓ or j/k move, Enter opens, / searches.
// With `groupBy`, rows sharing a key (e.g. a Shopify product's variants) sit under one header row:
// click it, Enter or → opens it, ← closes it. Searching opens every group with a match.
// With `sectionOf`, rows sit under labelled bands that never close (a PO's or a BOM's Packaging lines).
// With `editing`, cells that define `edit` (or `groupEdit` on header rows) edit like a spreadsheet:
// click to edit, Enter saves and moves down, Tab / Shift+Tab move across, Esc cancels.
// With `selection`, rows get checkboxes (shift-click for a range, a header row selects its group); `selectable` leaves
// some rows without one (nothing to do to them), and select-all and ranges skip them.
// With `fit`, the table is as tall as its rows (up to `fit` rows, default 12, then it scrolls inside), in a bordered box:
// the table inside a panel, a tab or a card that scrolls as a page (a PO's bills, a run's costs), where `h-full` has no
// height to fill. A column's `total` puts a totals row under the rows (over the rows as filtered), kept in sight.
// With `exportName`, the table has the person's own views (lib/table-views.ts, per person on any computer): View (pick
// a saved view, save what the table shows as one, Save / Discard changes) and Columns (tick which show; search and sort
// still see them all), first in the table's bar; a header drags sideways to move its column and its edge to resize it.
// **A table is one unit** (Mitch, 2026-11-04: "Tables should be a unit, where the relevant controls etc are attached to
// the table. This is a global rule"): its bar (View, Columns, then its search and filters, its own buttons on the
// right: `bar`) sits on top of its rows, and Copy / CSV and its count under them, never in a page header with tabs or
// notices between. And the footer has Copy (for Sheets) and CSV: the rows as filtered and sorted, each column's
// `text` (else its sort value: numbers stay numbers), columns without a header left out.
import { useVirtualizer } from "@tanstack/react-virtual";
import { ArrowDown, ArrowUp, ChevronRight } from "lucide-react";
import { type KeyboardEvent, type ReactNode, type PointerEvent as ReactPointerEvent, type SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import type { TableView } from "../../../shared/db.gen.ts";
import { navigate, useSearch } from "../lib/router.tsx";
import { type Layout, layoutOf, type Sort, sortOf, useTableViews, viewSearch, withViewSearch } from "../lib/table-views.ts";
import { cx, Select } from "./controls.tsx";
import { ExportButtons } from "./ExportButtons.tsx";
import { ColumnsMenu, pinnedWidths, ViewMenu } from "./TableViews.tsx";
import { useTableText } from "./table-text.ts";

type Editor<A> = {
  /** A dropdown instead of a text box; its choices can follow the row (a part group's Category, by its Type). */
  options?: readonly { value: string; label: string }[] | ((target: A) => readonly { value: string; label: string }[]);
  /** A date picker (its text is YYYY-MM-DD, empty for none) instead of a text box. */
  type?: "date";
  get: (target: A) => string;
  save: (target: A, text: string) => void;
  /** Only some rows can be edited (e.g. a product's title on single-variant rows). */
  can?: (target: A) => boolean;
};

export type Column<T> = {
  key: string;
  header: string;
  /** A hover on the header (what the column means, where its numbers come from). */
  headerTitle?: string;
  /** CSS grid track, e.g. "2fr", "120px", "minmax(160px,1fr)". */
  width?: string;
  /** Sort and search value. Defaults to row[key]. */
  value?: (row: T) => string | number | boolean | null | undefined;
  /** What Copy and CSV put in the cell, when the sort value isn't it (a status's rank, a PO number's digits). */
  text?: (row: T) => string | number | null | undefined;
  /** `nested` is true for a row shown under its group's header. */
  cell?: (row: T, ctx: { nested: boolean }) => ReactNode;
  /** The cell on a group's header row (tables with groupBy). */
  groupCell?: (rows: T[], ctx: { open: boolean }) => ReactNode;
  align?: "left" | "right";
  /** Editable in edit mode (a row's cell). */
  edit?: Editor<T>;
  /** Editable in edit mode (a group header's cell, e.g. a product's title). */
  groupEdit?: Editor<T[]>;
  /** Hidden until someone shows it in Columns (kept in their layout's `shown`): detail most people don't need. */
  optional?: boolean;
  /** Its cell in the totals row (over the rows as filtered); a table with any shows one under its rows. */
  total?: (rows: T[]) => ReactNode;
};

/** A table's bar, on top of its rows (after View and Columns): its search, its filters, and its own buttons on the right. */
export type TableBar = { search?: ReactNode; filters?: ReactNode; actions?: ReactNode };

type Line<T> =
  | { kind: "row"; row: T; nested: boolean }
  | { kind: "group"; key: string; rows: T[]; open: boolean }
  | { kind: "section"; key: string; label: ReactNode; count: number };

/** A cell's own controls (buttons, links, a picker, a popover, a checkbox): their clicks and keys are theirs, not the
 * row's (opening it) or the table's (Enter, Space and j/k move or open; typed into a popover inside the cell, they'd go
 * there too). Wrap them in a cell's content: `<CellControls><Button …/></CellControls>`. */
const stopEvent = (e: SyntheticEvent) => e.stopPropagation();
export function CellControls({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cx("flex min-w-0 items-center", className)} onClick={stopEvent} onKeyDown={stopEvent}>
      {children}
    </span>
  );
}

/** A labelled band rows are kept under (always open): e.g. a BOM's or a PO's Packaging lines. Sections show in `order`. */
export type TableSection = { key: string; label: ReactNode; order: number };

const ROW_H = 34;
const HEAD_H = 33;

/** The narrowest the columns fit in: fixed widths plus minmax() minimums (a bare "fr" track can shrink to nothing). */
function minWidth(tracks: string[]) {
  return tracks.reduce((sum, t) => sum + Number(/^(?:minmax\(\s*)?(\d+(?:\.\d+)?)px/.exec(t)?.[1] ?? 0), 0);
}

/** The columns hidden on a table in this browser before table views followed the person (read until their own layout
 * is first saved; nothing when it has no name, or storage is blocked). */
function readHidden(name: string | undefined): ReadonlySet<string> {
  if (!name) return new Set();
  try {
    const keys = JSON.parse(localStorage.getItem(`hhl-ops.columns.${name}`) ?? "[]");
    return new Set(Array.isArray(keys) ? keys.filter((k) => typeof k === "string") : []);
  } catch {
    return new Set();
  }
}

/** A cell for Copy / CSV: the column's text, else its value (true reads "yes", false nothing). */
function exportCell<T>(col: Column<T>, row: T): unknown {
  const v = col.text ? col.text(row) : get(col, row);
  return v === true ? "yes" : v === false ? "" : v;
}

function get<T>(col: Column<T>, row: T) {
  return (col.value ? col.value(row) : (row as Record<string, unknown>)[col.key]) as string | number | boolean | null | undefined;
}

export function DataTable<T extends { id: string }>({
  rows,
  columns: allColumns,
  search = "",
  activeId,
  onOpen,
  defaultSort,
  empty,
  groupBy,
  groupNoun = ["group", "groups"],
  rowNoun = ["row", "rows"],
  editing = false,
  selection,
  selectable,
  sectionOf,
  exportName,
  bar,
  pageFilters = true,
  fit,
}: {
  rows: T[];
  columns: Column<T>[];
  search?: string;
  activeId?: string | null;
  onOpen?: (row: T) => void;
  defaultSort?: { key: string; desc?: boolean };
  empty?: ReactNode;
  /** Rows with the same key are grouped; null (or a group of one) stays a plain row. */
  groupBy?: (row: T) => string | null;
  groupNoun?: [string, string];
  rowNoun?: [string, string];
  editing?: boolean;
  selection?: { selected: Set<string>; onChange: (next: Set<string>) => void };
  /** Which rows can be ticked (default all): the others get no checkbox, and select-all and ranges skip them. */
  selectable?: (row: T) => boolean;
  /** Rows sorted within labelled bands that never close (with a header line each); null keeps a row out of any band. */
  sectionOf?: (row: T) => TableSection | null;
  /** The table's name for Copy / CSV in the footer (and the file's name); no buttons without it. It also names the
   * table's views and layout (Table views). */
  exportName?: string;
  /** The table's bar, on top of its rows: after View and Columns its `search` box and `filters`, and on the right its own
   * `actions` (what acts on these rows: New run, Link a PO, Pick again). */
  bar?: TableBar;
  /** Table views --- whether its views keep the page's filters and tab: not for a table inside a panel (a PO's lines),
   * whose page's filters belong to the list beside it. */
  pageFilters?: boolean;
  /** As tall as its rows, up to this many (true: 12), then it scrolls inside; in a bordered box. For a table inside a
   * panel or a page that scrolls, where there's no height to fill. */
  fit?: boolean | number;
}) {
  // Table views --- the person's working layout of this table (columns shown, their order and widths, the sort) and
  // their saved views (lib/table-views.ts), kept per page and tab as well as the table's name, so lists that share a
  // title (three "Specs" tables) keep their own. The first column is always shown and first. A table without a name
  // keeps its sort for the visit.
  const params = useSearch();
  const text = useTableText();
  const tab = params.get("tab") ?? "";
  const storeKey = exportName && typeof location !== "undefined" ? `${location.pathname}${tab ? `#${tab}` : ""}:${exportName}` : undefined;
  const tv = useTableViews(storeKey);
  // Until the person's own layout is first saved, what this browser hid before views followed the person
  const ownLayout: Layout = tv.pref ? layoutOf(tv.pref) : { hidden: [...readHidden(storeKey)] };
  const [dragged, setDragged] = useState<Layout | null>(null); // while a column or its edge is being dragged
  const layout = dragged ?? ownLayout;
  // Optional columns are hidden until shown.
  const hidden = useMemo(() => {
    const shownKeys = new Set(layout.shown ?? []);
    return new Set([...(layout.hidden ?? []), ...allColumns.filter((c) => c.optional && !shownKeys.has(c.key)).map((c) => c.key)]);
  }, [layout.hidden, layout.shown, allColumns]);
  const arranged = useMemo(() => arrange(allColumns, layout.order), [allColumns, layout.order]);
  const columns = useMemo(() => arranged.filter((c, i) => i === 0 || !hidden.has(c.key)), [arranged, hidden]);
  const saveLayout = (next: Layout) => tv.savePref({ layout: next });
  const toggleColumn = (key: string, on: boolean) => {
    const optional = allColumns.find((c) => c.key === key)?.optional;
    const shownKeys = (layout.shown ?? []).filter((k) => k !== key);
    const hiddenKeys = (layout.hidden ?? []).filter((k) => k !== key);
    saveLayout({
      ...layout,
      hidden: on || optional ? hiddenKeys : [...hiddenKeys, key],
      ...(optional || layout.shown ? { shown: on && optional ? [...shownKeys, key] : shownKeys } : {}),
    });
  };
  const firstSort: Sort = defaultSort ?? { key: allColumns[0].key, desc: false };
  const [visitSort, setVisitSort] = useState(firstSort);
  const savedSort = sortOf(tv.pref);
  const sort = tv.enabled ? (savedSort && allColumns.some((c) => c.key === savedSort.key) ? savedSort : firstSort) : visitSort;
  const setSort = (next: Sort) => (tv.enabled ? tv.savePref({ sort: next }) : setVisitSort(next));

  // The view picked and whether the table still looks like it: its layout, sort, and the page's filters and tab.
  const filters = pageFilters ? viewSearch(params.toString()) : "";
  const tabOnly = pageFilters ? viewSearch(tab ? `tab=${tab}` : "") : "";
  const picked = tv.views.find((v) => v.id === tv.pref?.view_id);
  const shape = (l: Layout, s: Sort | null, f: string) =>
    JSON.stringify([normalLayout(allColumns, l), { key: (s ?? firstSort).key, desc: !!(s ?? firstSort).desc }, f]);
  const changed = tv.enabled && shape(layout, sort, filters) !== (picked ? shape(layoutOf(picked), sortOf(picked), picked.search) : shape({}, null, tabOnly));
  const pick = (v?: TableView) => {
    tv.savePref({ view_id: v?.id ?? null, layout: v ? layoutOf(v) : {}, sort: v ? sortOf(v) : null });
    if (pageFilters) navigate(location.pathname + withViewSearch(location.search, v ? v.search : tabOnly), { replace: true });
  };
  // Back on the page with no filters, the view picked last brings its own back (a link with filters keeps them).
  const restored = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!storeKey || !pageFilters || restored.current === storeKey) return;
    restored.current = storeKey;
    if (picked && picked.search !== tabOnly && filters === tabOnly)
      navigate(location.pathname + withViewSearch(location.search, picked.search), { replace: true });
  }, [storeKey, pageFilters, picked, filters, tabOnly]);
  const menus = tv.enabled && storeKey && (
    <>
      <ViewMenu
        views={tv.views}
        picked={picked}
        changed={changed}
        onPick={pick}
        onSave={() => picked && void tv.updateView(picked.id, { layout, sort, search: filters })}
        onSaveAs={async (name) => {
          const id = crypto.randomUUID();
          const ok = await tv.createView({ id, table_key: storeKey, name, layout, sort, search: filters, position: tv.views.length });
          if (ok) tv.savePref({ view_id: id });
          return ok;
        }}
        onRename={(v, name) => tv.updateView(v.id, { name })}
        onDelete={(v) => tv.removeView(v.id)}
        onDiscard={() => pick(picked)}
      />
      <ColumnsMenu
        columns={arranged}
        hidden={hidden}
        onToggle={toggleColumn}
        onShowAll={() => saveLayout({ ...layout, hidden: [], shown: allColumns.filter((c) => c.optional).map((c) => c.key) })}
        onReset={layout.order?.length || Object.keys(layout.widths ?? {}).length ? () => saveLayout({ hidden: layout.hidden, shown: layout.shown }) : undefined}
      />
    </>
  );

  // A header drags sideways to move its column (a click still sorts), its right edge to resize it (a double-click there
  // gives it back its own width).
  const headRef = useRef<HTMLDivElement>(null);
  const [moving, setMoving] = useState<{ key: string; to: number } | null>(null);
  const justMoved = useRef(false);
  const startMove = (e: ReactPointerEvent, key: string, index: number) => {
    if (!tv.enabled || index === 0 || e.button !== 0) return;
    const x0 = e.clientX;
    let to: number | null = null;
    const move = (ev: PointerEvent) => {
      if (to === null && Math.abs(ev.clientX - x0) < 6) return;
      // Before the first column whose middle is right of the pointer; never before the first column
      const cells = [...(headRef.current?.querySelectorAll<HTMLElement>("[data-col]") ?? [])].map((el) => el.getBoundingClientRect());
      const i = cells.findIndex((r) => ev.clientX < r.left + r.width / 2);
      to = Math.max(1, i === -1 ? cells.length : i);
      setMoving({ key, to });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setMoving(null);
      if (to === null) return;
      justMoved.current = true;
      setTimeout(() => {
        justMoved.current = false;
      });
      const shown = columns.map((c) => c.key);
      const before = shown[to];
      if (before === key || shown[to - 1] === key) return; // where it already is
      const order = arranged.map((c) => c.key).filter((k) => k !== key);
      order.splice(before === undefined ? order.length : order.indexOf(before), 0, key);
      saveLayout({ ...layout, order: order.slice(1) });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const startResize = (e: ReactPointerEvent<HTMLElement>, key: string) => {
    if (!tv.enabled) return;
    e.preventDefault();
    e.stopPropagation();
    const base = layout;
    // Its edge follows the pointer: it and the columns before it keep the widths they're drawn at (pinnedWidths()).
    const pinned = pinnedWidths(e.currentTarget, key, base.widths);
    const w0 = pinned[key] ?? Math.round((e.currentTarget.parentElement as HTMLElement).getBoundingClientRect().width);
    const x0 = e.clientX;
    let w = w0;
    const withWidth = (px: number): Layout => ({ ...base, widths: { ...pinned, [key]: px } });
    const move = (ev: PointerEvent) => {
      w = Math.max(48, Math.min(2000, Math.round(w0 + ev.clientX - x0)));
      setDragged(withWidth(w));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setDragged(null);
      if (w !== w0) saveLayout(withWidth(w));
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const ownWidth = (key: string) => {
    const { [key]: _, ...widths } = layout.widths ?? {};
    saveLayout({ ...layout, widths });
  };
  const [cursor, setCursor] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  // The cell being edited, by its line's key (lineKey()), not its place: a save can move the line (a new sort value, or
  // another band), and the edit stays with it.
  const [edit, setEdit] = useState<{ key: string; col: string; draft: string; session: number } | null>(null);
  // Each cell edit is a session; an input only ever saves its own session, once (a blur after Enter can't save twice).
  const session = useRef(0);
  const saved = useRef(-1);
  const anchor = useRef<number | null>(null); // last checkbox clicked, for shift-click ranges
  const scrollRef = useRef<HTMLDivElement>(null);
  const searching = search.trim() !== "";

  const sorted = useMemo(() => {
    const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
    const filtered = terms.length
      ? rows.filter((r) => {
          const hay = allColumns
            .map((c) => String(get(c, r) ?? ""))
            .join(" ")
            .toLowerCase();
          return terms.every((t) => hay.includes(t));
        })
      : rows;
    const col = allColumns.find((c) => c.key === sort.key) ?? allColumns[0];
    const dir = sort.desc ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const x = get(col, a);
      const y = get(col, b);
      if (x == null || x === "") return 1;
      if (y == null || y === "") return -1;
      return (
        (typeof x === "string" && typeof y === "string" ? x.localeCompare(y, undefined, { numeric: true, sensitivity: "base" }) : x < y ? -1 : x > y ? 1 : 0) *
        dir
      );
    });
  }, [rows, allColumns, search, sort]);

  // The open record's group is always open, so it can be seen and stepped through.
  const activeGroup = useMemo(() => {
    const row = activeId && groupBy ? rows.find((r) => r.id === activeId) : undefined;
    return row && groupBy ? groupBy(row) : null;
  }, [activeId, rows, groupBy]);

  const lines = useMemo<Line<T>[]>(() => {
    if (sectionOf) {
      // Bands in their order, each with its rows as sorted; rows outside any band first.
      const bands = new Map<string, { section: TableSection; rows: T[] }>();
      const loose: T[] = [];
      for (const row of sorted) {
        const s = sectionOf(row);
        if (!s) loose.push(row);
        else if (bands.has(s.key)) bands.get(s.key)?.rows.push(row);
        else bands.set(s.key, { section: s, rows: [row] });
      }
      const out: Line<T>[] = loose.map((row) => ({ kind: "row", row, nested: false }));
      for (const { section, rows: members } of [...bands.values()].sort((a, b) => a.section.order - b.section.order)) {
        out.push({ kind: "section", key: section.key, label: section.label, count: members.length });
        for (const row of members) out.push({ kind: "row", row, nested: false });
      }
      return out;
    }
    if (!groupBy) return sorted.map((row) => ({ kind: "row", row, nested: false }));
    // Groups appear where their first row sorts, so sorting by price sorts products by price.
    const groups = new Map<string, T[]>();
    const order: (string | T)[] = [];
    for (const row of sorted) {
      const key = groupBy(row);
      if (key === null) order.push(row);
      else if (groups.has(key)) groups.get(key)?.push(row);
      else {
        groups.set(key, [row]);
        order.push(key);
      }
    }
    const out: Line<T>[] = [];
    for (const entry of order) {
      if (typeof entry !== "string") out.push({ kind: "row", row: entry, nested: false });
      else {
        const members = groups.get(entry) ?? [];
        if (members.length === 1) out.push({ kind: "row", row: members[0], nested: false });
        else {
          const open = searching || expanded.has(entry) || entry === activeGroup;
          out.push({ kind: "group", key: entry, rows: members, open });
          if (open) for (const row of members) out.push({ kind: "row", row, nested: true });
        }
      }
    }
    return out;
  }, [sorted, groupBy, expanded, searching, activeGroup, sectionOf]);

  // The header scrolls sideways with the rows and sticks to the top: the rows start below it, and a row scrolled to sits
  // just under it rather than behind it.
  const virtualizer = useVirtualizer({
    count: lines.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_H,
    overscan: 15,
    scrollMargin: HEAD_H,
    scrollPaddingStart: HEAD_H,
  });

  // Keep the keyboard cursor on the open record.
  useEffect(() => {
    if (!activeId) return;
    const i = lines.findIndex((l) => l.kind === "row" && l.row.id === activeId);
    if (i >= 0) setCursor(i);
  }, [activeId, lines]);

  // Leaving edit mode drops any half-typed cell.
  useEffect(() => {
    if (!editing) setEdit(null);
  }, [editing]);

  const toggle = (key: string, open?: boolean) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (open ?? !next.has(key)) next.add(key);
      else next.delete(key);
      return next;
    });

  const move = (i: number, dir: 1 | -1 = i < cursor ? -1 : 1) => {
    let next = Math.max(0, Math.min(lines.length - 1, i));
    // A band's header isn't a stop: go on to the next line that is, the way the cursor was going (else back).
    while (lines[next]?.kind === "section" && next + dir >= 0 && next + dir < lines.length) next += dir;
    if (lines[next]?.kind === "section") while (lines[next]?.kind === "section" && next - dir >= 0 && next - dir < lines.length) next -= dir;
    if (lines[next]?.kind === "section") return;
    setCursor(next);
    virtualizer.scrollToIndex(next, { align: "auto" });
    // While a record is open, moving the cursor switches to the next record (like an inbox).
    const line = lines[next];
    if (activeId && line?.kind === "row") onOpen?.(line.row);
  };

  const groupOf = (row: T) => (groupBy ? groupBy(row) : null);

  // Editing ------------------------------------------------------------------------------------

  const editorFor = (line: Line<T> | undefined, col: Column<T>) => {
    if (!line || line.kind === "section") return undefined;
    if (line.kind === "group") return col.groupEdit && (!col.groupEdit.can || col.groupEdit.can(line.rows)) ? col.groupEdit : undefined;
    return col.edit && (!col.edit.can || col.edit.can(line.row)) ? col.edit : undefined;
  };
  const currentText = (line: Line<T>, col: Column<T>) =>
    line.kind === "section" ? "" : line.kind === "group" ? (col.groupEdit?.get(line.rows) ?? "") : (col.edit?.get(line.row) ?? "");
  const lineKey = (line: Line<T>) => (line.kind === "row" ? `r:${line.row.id}` : `${line.kind === "group" ? "g" : "s"}:${line.key}`);
  const editIndex = edit ? lines.findIndex((l) => lineKey(l) === edit.key) : -1;
  // A line that's gone (removed, or filtered out) takes its half-typed cell with it, so the keys work again.
  useEffect(() => {
    if (edit && editIndex < 0) setEdit(null);
  }, [edit, editIndex]);

  const startEdit = (lineIndex: number, colKey: string) => {
    const line = lines[lineIndex];
    const col = columns.find((c) => c.key === colKey);
    if (!editing || !line || !col || !editorFor(line, col)) return false;
    setCursor(lineIndex);
    session.current++;
    setEdit({ key: lineKey(line), col: colKey, draft: currentText(line, col), session: session.current });
    return true;
  };

  const commit = (draft: string, at: { key: string; col: string; session: number }) => {
    if (at.session !== session.current || saved.current === at.session) return;
    saved.current = at.session;
    const line = lines.find((l) => lineKey(l) === at.key);
    const col = columns.find((c) => c.key === at.col);
    if (line && col && draft !== currentText(line, col)) {
      if (line.kind === "group") col.groupEdit?.save(line.rows, draft);
      else if (line.kind === "row") col.edit?.save(line.row, draft);
    }
    setEdit(null);
  };

  /** Saves and moves: down/up to the next line with an editor in this column, or across this line. */
  const commitAndMove = (draft: string, dir: "down" | "up" | "right" | "left", from: { key: string; col: string; session: number }) => {
    if (from.session !== session.current) return;
    // The next cell is picked from the lines as they are before the save (it may move the line), and kept by its key.
    const at = lines.findIndex((l) => lineKey(l) === from.key);
    commit(draft, from);
    if (at < 0) return;
    if (dir === "down" || dir === "up") {
      const step = dir === "down" ? 1 : -1;
      const col = columns.find((c) => c.key === from.col);
      for (let i = at + step; i >= 0 && i < lines.length; i += step) {
        if (col && editorFor(lines[i], col)) {
          virtualizer.scrollToIndex(i, { align: "auto" });
          startEdit(i, from.col);
          return;
        }
      }
    } else {
      const editable = columns.filter((c) => editorFor(lines[at], c));
      const i = editable.findIndex((c) => c.key === from.col);
      const next = editable[i + (dir === "right" ? 1 : -1)];
      if (next) startEdit(at, next.key);
    }
    scrollRef.current?.focus();
  };

  // Selection ----------------------------------------------------------------------------------

  const rowIndexes = useMemo(() => lines.flatMap((l, i) => (l.kind === "row" ? [i] : [])), [lines]);
  const canPick = (row: T) => !selectable || selectable(row);
  const pickable = useMemo(() => new Set(selectable ? rows.filter(selectable).map((r) => r.id) : rows.map((r) => r.id)), [rows, selectable]);
  const setSelected = (ids: string[], on: boolean) => {
    if (!selection) return;
    const next = new Set(selection.selected);
    for (const id of ids) if (pickable.has(id) || !on) on ? next.add(id) : next.delete(id);
    selection.onChange(next);
  };
  const clickCheckbox = (lineIndex: number, shift: boolean) => {
    const line = lines[lineIndex];
    if (!selection || !line || line.kind === "section") return;
    if (line.kind === "group") {
      const ids = line.rows.filter(canPick).map((r) => r.id);
      setSelected(ids, !ids.every((id) => selection.selected.has(id)));
      return;
    }
    if (!canPick(line.row)) return;
    const on = !selection.selected.has(line.row.id);
    if (shift && anchor.current !== null) {
      const [a, b] = [Math.min(anchor.current, lineIndex), Math.max(anchor.current, lineIndex)];
      setSelected(
        rowIndexes.filter((i) => i >= a && i <= b).map((i) => (lines[i] as { row: T }).row.id),
        on,
      );
    } else setSelected([line.row.id], on);
    anchor.current = lineIndex;
  };
  const allIds = useMemo(() => sorted.filter((r) => pickable.has(r.id)).map((r) => r.id), [sorted, pickable]);
  const allSelected = !!selection && allIds.length > 0 && allIds.every((id) => selection.selected.has(id));

  const onKeyDown = (e: KeyboardEvent) => {
    if (edit) return; // the cell's input handles its own keys
    if ((e.target as HTMLElement).closest("[data-head]")) return; // Enter sorts, Space selects all: not the cursor row's
    if ((e.target as HTMLElement).closest("input, textarea, select, [contenteditable]")) return; // typing in a cell's own box
    const line = lines[cursor];
    if (e.key === "ArrowDown" || e.key === "j") move(cursor + 1);
    else if (e.key === "ArrowUp" || e.key === "k") move(cursor - 1);
    else if (e.key === "Home") move(0);
    else if (e.key === "End") move(lines.length - 1);
    else if (e.key === "PageDown") move(cursor + 15);
    else if (e.key === "PageUp") move(cursor - 15);
    else if (e.key === "ArrowRight" && line?.kind === "group") toggle(line.key, true);
    else if (e.key === "ArrowLeft" && line?.kind === "group") toggle(line.key, false);
    else if (e.key === "ArrowLeft" && line?.kind === "row" && line.nested) {
      const key = groupOf(line.row);
      if (key) {
        toggle(key, false);
        setCursor(lines.findIndex((l) => l.kind === "group" && l.key === key));
      }
    } else if (e.key === " " && selection && line) clickCheckbox(cursor, e.shiftKey);
    else if (e.key === "Enter" && line && line.kind !== "section") line.kind === "group" ? toggle(line.key) : onOpen?.(line.row);
    else return;
    e.preventDefault();
  };

  const trackList = [
    ...(selection ? ["32px"] : []),
    ...columns.map((c) => (layout.widths?.[c.key] ? `${layout.widths[c.key]}px` : (c.width ?? "minmax(120px,1fr)"))),
  ];
  const tracks = trackList.join(" ");
  const groupCount = groupBy ? new Set(sorted.map(groupOf).filter(Boolean)).size + sorted.filter((r) => !groupOf(r)).length : 0;
  const plural = (n: number, [one, many]: [string, string]) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

  const checkbox = (checked: boolean, partial: boolean, onClick: (shift: boolean) => void, label: string) => (
    <div className="flex items-center justify-center">
      <input
        type="checkbox"
        aria-label={label}
        checked={checked}
        ref={(el) => {
          if (el) el.indeterminate = partial;
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClick(e.shiftKey);
        }}
        onChange={() => {}}
        className="accent-[var(--accent)]"
      />
    </div>
  );

  const cellEditor = (col: Column<T>, editor: Editor<never>, target: T | T[]) => {
    if (!edit) return null;
    const at = edit;
    const options = typeof editor.options === "function" ? editor.options(target as never) : editor.options;
    const keys = (e: KeyboardEvent<HTMLInputElement | HTMLButtonElement>) => {
      const draft = (e.currentTarget as HTMLInputElement).value;
      if (e.key === "Enter") commitAndMove(draft, e.shiftKey ? "up" : "down", at);
      else if (e.key === "Tab") commitAndMove(draft, e.shiftKey ? "left" : "right", at);
      else if (e.key === "Escape") {
        saved.current = at.session;
        setEdit(null);
        scrollRef.current?.focus();
      } else return;
      e.preventDefault();
      e.stopPropagation();
    };
    const cls = cx(
      "h-7 w-full rounded border border-accent bg-surface text-[13px] focus:outline-none",
      editor.type === "date" ? "px-1" : "px-2",
      col.align === "right" && "text-right",
    );
    return options ? (
      // Opens straight away; choosing saves, Esc (or the same choice) leaves the cell as it was.
      <Select
        defaultOpen
        defaultValue={edit.draft}
        options={options}
        onChange={(e) => commit(e.target.value, at)}
        onBlur={(e) => commit(e.target.value, at)}
        onKeyDown={keys}
        onDismiss={() => {
          saved.current = at.session;
          setEdit(null);
          scrollRef.current?.focus();
        }}
        className={cls}
      />
    ) : (
      <input
        autoFocus
        type={editor.type ?? "text"}
        defaultValue={edit.draft}
        data-calc={col.align === "right" && !editor.type ? "" : undefined} // a number column: its "= result" hint (ui/CalcHint.tsx)
        onFocus={(e) => !editor.type && e.currentTarget.select()}
        onBlur={(e) => commit(e.target.value, at)}
        onKeyDown={keys}
        className={cls}
      />
    );
  };

  const totals = columns.some((c) => c.total);
  const fitRows = fit === true ? 12 : fit || 0;
  const hasBar = !!menus || !!bar?.search || !!bar?.filters || !!bar?.actions;
  return (
    <div className={cx("flex min-w-0 flex-col", fit ? "overflow-hidden rounded-md border border-line" : "h-full min-h-0")}>
      {/* The table's bar: what this table shows (View, Columns), narrowing it (search, filters), then its own buttons */}
      {hasBar && (
        <div data-table-bar className="flex min-h-10 shrink-0 flex-wrap items-center gap-2 border-b border-line bg-surface px-2 py-1.5">
          {menus && <div className="flex shrink-0 items-center gap-1">{menus}</div>}
          {bar?.search}
          {bar?.filters && <div className="flex min-w-0 flex-wrap items-center gap-2">{bar.filters}</div>}
          {bar?.actions && <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">{bar.actions}</div>}
        </div>
      )}
      {/* biome-ignore lint/a11y/useSemanticElements: virtualized rows are absolutely positioned, so they can't be a <table> */}
      <div
        ref={scrollRef}
        role="grid"
        aria-rowcount={lines.length}
        tabIndex={0}
        onKeyDown={onKeyDown}
        className={cx("@container relative min-h-0 overflow-auto focus:outline-none", !fit && "flex-1")}
        // As tall as the rows (and a sideways scrollbar), up to fitRows of them
        style={fit ? { maxHeight: HEAD_H + (fitRows + (totals ? 1 : 0)) * ROW_H + 14 } : undefined}
        data-table
      >
        {/* As wide as the box, or as the columns need: then the box scrolls sideways, header and rows together. */}
        <div style={{ minWidth: minWidth(trackList) }}>
          <div
            ref={headRef}
            data-head
            className="sticky top-0 z-10 grid border-b border-line bg-surface-2 text-[11px] font-medium tracking-wide text-muted uppercase"
            style={{ gridTemplateColumns: tracks, height: HEAD_H }}
          >
            {selection &&
              checkbox(
                allSelected,
                !allSelected && selection.selected.size > 0,
                () => setSelected(allIds, !allSelected),
                allSelected ? text.clearSelection : text.selectAll(allIds.length),
              )}
            {columns.map((c, i) => (
              <div key={c.key} data-col className={cx("relative flex min-w-0", moving?.key === c.key && "opacity-40")}>
                {moving && moving.to === i && <span className="absolute inset-y-1 -left-px z-10 w-0.5 bg-accent" />}
                {moving && moving.to === columns.length && i === columns.length - 1 && <span className="absolute inset-y-1 -right-px z-10 w-0.5 bg-accent" />}
                <button
                  type="button"
                  className={cx(
                    "flex min-w-0 flex-1 items-center gap-1 px-3 hover:text-text",
                    editing && (c.edit || c.groupEdit) && "text-accent-text",
                    tv.enabled && i > 0 && "cursor-grab active:cursor-grabbing",
                  )}
                  title={c.headerTitle}
                  onPointerDown={(e) => startMove(e, c.key, i)}
                  onClick={() => !justMoved.current && setSort({ key: c.key, desc: sort.key === c.key ? !sort.desc : false })}
                >
                  <span className="truncate">{c.header}</span>
                  {sort.key === c.key && (sort.desc ? <ArrowDown className="size-3 shrink-0" /> : <ArrowUp className="size-3 shrink-0" />)}
                </button>
                {/* A divider between headers, so it's clear where one column ends and its edge can be dragged */}
                {i < columns.length - 1 && <span aria-hidden className="pointer-events-none absolute inset-y-2 right-0 w-px bg-line-strong" />}
                {tv.enabled && (
                  <span
                    aria-hidden
                    data-resize-key={c.key}
                    title="Drag to resize · double-click for its own width"
                    onPointerDown={(e) => startResize(e, c.key)}
                    onDoubleClick={() => ownWidth(c.key)}
                    // Astride the column's edge, except the last column's, inside the table: one past it made every table a
                    // few pixels wider than its box, so it opened scrolled, the last column cut off (Mitch, 2026-11-04)
                    className={cx("absolute inset-y-0 z-10 w-2 cursor-col-resize hover:bg-accent/40", i === columns.length - 1 ? "right-0" : "-right-1")}
                  />
                )}
              </div>
            ))}
          </div>
          {lines.length === 0 ? (
            // Nothing to show: the message sits in the part of the table you can see (as wide as the scroll box, held at its
            // left edge), so a table wider than the page doesn't push it off to the right. Text gets the same room as the
            // rest (the vendor portal's "No parts yet…"), not the corner.
            <div className="sticky left-0 w-[100cqw]">
              {typeof empty === "string"
                ? empty && <div className="mx-auto max-w-xl px-6 py-10 text-center text-muted">{empty}</div>
                : (empty ?? <div className="p-6 text-center text-muted">{search ? "No matches" : "Nothing here yet"}</div>)}
            </div>
          ) : (
            <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
              {virtualizer.getVirtualItems().map((v) => {
                const line = lines[v.index];
                const base = "absolute inset-x-0 grid cursor-default items-center border-b border-line/60";
                const style = { top: v.start - HEAD_H, height: ROW_H, gridTemplateColumns: tracks };
                const cellFor = (c: Column<T>, i: number, content: ReactNode, editable: boolean) => {
                  const isEditing = editIndex === v.index && edit?.col === c.key;
                  return (
                    <div
                      key={c.key}
                      onClick={
                        editable
                          ? (e) => {
                              if (startEdit(v.index, c.key)) e.stopPropagation();
                            }
                          : undefined
                      }
                      className={cx(
                        "flex min-w-0 items-center gap-1.5 truncate px-3",
                        c.align === "right" && "justify-end tabular-nums",
                        // A row under a group (a product's variant, a part group's member) sits further in than the
                        // group's name, on a line down from its arrow; a row of its own lines up with group names.
                        i === 0 &&
                          groupBy &&
                          line.kind === "row" &&
                          (line.nested ? "relative pl-12 before:absolute before:inset-y-0 before:left-[18px] before:w-px before:bg-line" : "pl-8"),
                        editing && editable && !isEditing && "cursor-text hover:bg-accent-soft/40",
                        isEditing && "px-1",
                      )}
                    >
                      {isEditing
                        ? cellEditor(
                            c,
                            (line.kind === "group" ? c.groupEdit : c.edit) as Editor<never>,
                            line.kind === "group" ? line.rows : line.kind === "row" ? line.row : [],
                          )
                        : content}
                    </div>
                  );
                };
                if (line.kind === "section")
                  return (
                    <div
                      key={`s:${line.key}`}
                      className="absolute inset-x-0 flex items-end border-b border-line bg-surface-2/70 px-3 pb-1.5 text-[11px] font-medium tracking-wide text-muted uppercase"
                      style={{ top: v.start - HEAD_H, height: ROW_H }}
                    >
                      {/* Stays in sight while the table scrolls sideways. */}
                      <span className="sticky left-3 flex items-end gap-2">
                        {line.label}
                        <span className="font-normal text-faint normal-case">{line.count}</span>
                      </span>
                    </div>
                  );
                if (line.kind === "group") {
                  const ids = line.rows.map((r) => r.id);
                  const picked = selection ? ids.filter((id) => selection.selected.has(id)).length : 0;
                  return (
                    <div
                      key={`g:${line.key}`}
                      onMouseDown={() => setCursor(v.index)}
                      onClick={() => toggle(line.key)}
                      className={cx(base, "bg-surface-2/40 font-medium", v.index === cursor ? "bg-hover" : "hover:bg-hover")}
                      style={style}
                    >
                      {selection &&
                        checkbox(picked === ids.length, picked > 0 && picked < ids.length, () => clickCheckbox(v.index, false), "Select this product")}
                      {columns.map((c, i) =>
                        cellFor(
                          c,
                          i,
                          <>
                            {i === 0 && <ChevronRight className={cx("size-3.5 shrink-0 text-faint transition-transform", line.open && "rotate-90")} />}
                            {c.groupCell?.(line.rows, { open: line.open })}
                          </>,
                          !!editorFor(line, c),
                        ),
                      )}
                    </div>
                  );
                }
                const { row, nested } = line;
                return (
                  <div
                    key={row.id}
                    onMouseDown={() => setCursor(v.index)}
                    data-record=""
                    onClick={() => onOpen?.(row)}
                    className={cx(
                      base,
                      row.id === activeId
                        ? "bg-selected"
                        : selection?.selected.has(row.id)
                          ? "bg-accent-soft/50"
                          : v.index === cursor
                            ? "bg-hover"
                            : "hover:bg-hover",
                    )}
                    style={style}
                  >
                    {selection &&
                      (canPick(row) ? checkbox(selection.selected.has(row.id), false, (shift) => clickCheckbox(v.index, shift), text.selectRow) : <div />)}
                    {columns.map((c, i) => cellFor(c, i, c.cell ? c.cell(row, { nested }) : String(get(c, row) ?? ""), !!editorFor(line, c)))}
                  </div>
                );
              })}
            </div>
          )}
          {totals && lines.length > 0 && (
            <div
              data-totals
              className="sticky bottom-0 z-10 grid border-t border-line bg-surface-2 text-[12px] font-medium"
              style={{ gridTemplateColumns: tracks, height: ROW_H }}
            >
              {selection && <div />}
              {columns.map((c, i) => (
                <div
                  key={c.key}
                  className={cx("flex min-w-0 items-center truncate px-3", c.align === "right" && "justify-end tabular-nums", i === 0 && groupBy && "pl-8")}
                >
                  {c.total ? c.total(sorted) : i === 0 ? text.total : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="flex h-7 shrink-0 items-center gap-3 border-t border-line px-3 text-[11px] text-faint">
        <span>
          {groupBy
            ? `${plural(groupCount, groupNoun)} · ${sorted.length === rows.length ? plural(sorted.length, rowNoun) : text.of(plural(sorted.length, rowNoun), rows.length.toLocaleString())}`
            : sorted.length === rows.length
              ? plural(rows.length, rowNoun)
              : text.of(sorted.length.toLocaleString(), plural(rows.length, rowNoun))}
        </span>
        {editing && <span>{text.editHint}</span>}
        <span className="ml-auto flex items-center gap-2">
          {exportName && sorted.length > 0 && (
            <ExportButtons
              name={exportName}
              rows={() => {
                const cols = columns.filter((c) => c.header);
                return [cols.map((c) => c.header), ...sorted.map((r) => cols.map((c) => exportCell(c, r)))];
              }}
            />
          )}
        </span>
      </div>
    </div>
  );
}

/** The columns in the person's order (`order`: keys after the first, which stays first); a column the order doesn't know
 * yet (one added since) goes where it is in the code. */
function arrange<T>(all: Column<T>[], order: string[] | undefined): Column<T>[] {
  const [first, ...rest] = all;
  if (!first || !order?.length) return all;
  const byKey = new Map(rest.map((c) => [c.key, c]));
  const known = order.filter((k) => byKey.has(k));
  const placed = known.map((k) => byKey.get(k) as Column<T>);
  rest.forEach((c, i) => {
    if (!known.includes(c.key)) placed.splice(Math.min(i, placed.length), 0, c);
  });
  return [first, ...placed];
}

/** A layout as it shows on these columns, to compare (a view against the table now). */
function normalLayout<T>(all: Column<T>[], layout: Layout) {
  const keys = new Set(all.slice(1).map((c) => c.key));
  return {
    order: arrange(all, layout.order).map((c) => c.key),
    hidden: (layout.hidden ?? []).filter((k) => keys.has(k)).sort(),
    shown: (layout.shown ?? []).filter((k) => keys.has(k)).sort(),
    widths: Object.entries(layout.widths ?? {})
      .filter(([k]) => keys.has(k) || k === all[0]?.key)
      .sort(([a], [b]) => a.localeCompare(b)),
  };
}
